import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {TABS_COMPANY_DOCUMENTATION} from "../Model/Model";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {PROFILE_MODEL} from "../../../DocumentationApprovalFlow/Model/Model";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 05/12/2025
 *
 * Custom hook that manages the Company Documentation flow.
 * It handles step navigation, documentation status retrieval,
 * draft/save actions, and back navigation logic depending on
 * the active tab and user context.
 *
 * @param {string} profile - Profile identifier used to determine documentation flow behavior.
 * @returns {Object} Exposed state, refs, and handlers for the Company Documentation view.
 */
export const CompanyDocumentationApp = ({profile}) => {
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {state} = location;
    const backUrl = state?.backUrl;
    const {id} = useParams();
    const {user} = useSelector((state) => state.authenticatioauthennSlice);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [period, setPeriod] = useState(null);
    const [modified, setModified] = useState(false);
    const manageRef = useRef();
    const historyRef = useRef();
    const [documentationApproval, setDocumentationApproval] = useState(null);
    const userId = id || user?.data?.id;
    const userTypeId = user?.data?.userType?.id;

    /**
     * Effect: Initialize steps for navigation (Active, History).
     */
    useEffect(() => {
        setSteps([t("request"), t("history")]);
    }, []);


    /**
     * Handle step change (Active <-> History):
     * - Reset search results and filters
     * - Update active step
     * - Change subview accordingly
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    /**
     * Triggers a draft save action on the active tab.
     * Delegates the action to the referenced child component.
     */
    const handleSaveDraft = () => {
        switch (activeStep) {
            case TABS_COMPANY_DOCUMENTATION.REQUEST.INDEX:
                if (manageRef.current)
                manageRef.current.handleSaveDraft();
                break;
        }
    };

    /**
     * Triggers a submit/save action on the active tab.
     * Delegates the action to the referenced child component.
     */
    const handleSubmitSave = () => {
        switch (activeStep) {
            case TABS_COMPANY_DOCUMENTATION.REQUEST.INDEX:
                if (manageRef.current)
                    manageRef.current.handleSubmitSave();
                break;
        }
    };

    /**
     * Handles back navigation using the stored backUrl,
     * if it exists in the navigation state.
     */
    const handleOnBack = () => {
        if(backUrl) navigate(backUrl);
    }

    const titleName = profile === PROFILE_MODEL.COMPANY ? 'company_documentation' : profile === PROFILE_MODEL.VEHICLE ? 'vehicle_company' : 'employee_documentation';


    return {
        userId,
        userTypeId,
        steps,
        activeStep,
        setActiveStep,
        handleStepChange,
        t,
        permission,
        DEV,
        modified,
        setModified,
        manageRef,
        historyRef,
        handleSaveDraft,
        handleSubmitSave,
        documentationApproval,
        handleOnBack,
        period,
        titleName,
        setPeriod,
        setDocumentationApproval
    };
};
